/* eslint-disable react/jsx-props-no-spreading */
import React, { createRef } from 'react';
import {
  Route,
  RouteComponentProps,
  Switch,
  withRouter,
} from 'react-router-dom';
import styled from 'styled-components';
import { LicenseManager } from 'ag-grid-enterprise';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { GlobalContext } from '@mummssoftware/global-context/GlobalContext';
import {
  HeightProvider,
  PrintFooter,
  PatientHeader,
  TopBar,
} from '@mummssoftware/common-ui';
import { Pim } from '@mummssoftware/utils';
import { isSmallTouchDevice, safariDetect } from '@mummssoftware/utils/web';
import { NotFound, EnvIssue } from '../NotFound';
import DashboardComponent from '../Dashboard';
import {
  emptyDashboardWidgets,
  resolveLayoutWidgets,
  resolvePaletteWidgets,
} from '../Context/widgetConfig';
import { Config, DashboardType } from '../Context/types';
import {
  clinicalDashboard,
  patientDashboard,
  patientPortal,
} from '../Context/constants';

LicenseManager &&
  LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_KEY as string);

type BaseAppProps = {
  embedded?: boolean;
  currentUser: mumms.User;
  logout: () => void;
  keycloak: Keycloak.KeycloakInstance;
  patient?: mumms.patient;
  patientError?: mumms.PatientError;
  pim: Pim;
  config: Config | null;
  dashboardType: DashboardType;
};
type AppProps = BaseAppProps & RouteComponentProps;

// avoid excessive scrolling height
const AppRoot = styled.div<{ showHeader?: boolean }>`
  margin-left: var(--safe-area-inset-left);
  margin-right: var(--safe-area-inset-right);
  overflow: hidden;
  ${(props) => (props.showHeader ? `height: 100vh;` : `;`)}
  display: flex;
  /* Temp fix for viewport issue on ipads when smartchart is embedded in PIM */
  @media print {
    visibility: hidden;
    overflow: visible;
  }
`;

const AppContent = styled.div<{
  height: number;
  showHeader?: boolean;
  isWidgetOpen?: boolean;
}>`
  margin-top: ${(props) => props.height}px;
  width: 100%;
  ${(props) => (props.isWidgetOpen ? `position: fixed;` : `;`)}
  ${(props) =>
    props.showHeader
      ? `height: calc(100vh - ${props.height}px);
         overflow: auto;`
      : `;`}
  @media print {
    visibility: hidden;
    overflow: visible;
  }
`;

class App extends React.PureComponent<AppProps> {
  headerRef = createRef<HTMLElement>();

  render() {
    const {
      currentUser,
      keycloak,
      patient,
      pim,
      logout,
      config,
      patientError,
      dashboardType,
    } = this.props;
    const query = new URLSearchParams(this.props.location.search);
    // TODO const patientheader
    const showHeader = query.get('patientheader') === 'true';
    const errorConfig = {
      widgets: {
        patientDashboard: emptyDashboardWidgets(),
        clinicalDashboard: emptyDashboardWidgets(),
      },
    };
    const dashboardWidgets = config?.widgets ?? errorConfig.widgets;

    const patientLayout = resolveLayoutWidgets(
      dashboardWidgets.patientDashboard.layout,
    );
    const clinicalLayout = resolveLayoutWidgets(
      dashboardWidgets.clinicalDashboard.layout,
    );
    const patientPalette = resolvePaletteWidgets(
      dashboardWidgets.patientDashboard.palette,
      dashboardWidgets.patientDashboard.layout,
    );
    const clinicalPalette = resolvePaletteWidgets(
      dashboardWidgets.clinicalDashboard.palette,
      dashboardWidgets.clinicalDashboard.layout,
    );
    /** api/cache: config-service path (add/remove + persist). */
    const hasConfigServiceDashboardWidgets =
      config?.configSource === 'api' || config?.configSource === 'cache';

    const showTopBar = false;

    return (
      <GlobalContext.Consumer>
        {({ widgetsOpen }) => {
          const isWidgetOpen = !!widgetsOpen.length;
          return (
            <AppRoot id="dashboard-app-root" showHeader={showHeader}>
              <div id="portal-container" />
              {showHeader && patient && (
                <PatientHeader ref={this.headerRef} patient={patient} />
              )}
              {showTopBar && (
                <TopBar
                  ref={this.headerRef}
                  title="SmartChart"
                  subtitle={
                    patient ? `${patient.firstName} ${patient.lastName}` : null
                  }
                  // disableSitePicker
                  currentUser={currentUser}
                  logout={logout}
                />
              )}
              <HeightProvider passedRef={this.headerRef}>
                {(height: number) => (
                  <AppContent
                    id="dashboard-app-content"
                    height={height}
                    showHeader={showHeader}
                    isWidgetOpen={isWidgetOpen}
                  >
                    <Switch>
                      {patientError && (
                        <Route
                          path="/"
                          render={(props: any) => <NotFound {...props} />}
                        />
                      )}
                      {!config && (
                        <Route path="/" render={() => <EnvIssue />} />
                      )}
                      {/* PATIENT Dashboard or CLINICAL dashboard */}
                      {dashboardType === patientDashboard && (
                        <Route
                          path="/"
                          render={(props: any) => (
                            <DashboardComponent
                              pim={pim}
                              keycloak={keycloak}
                              logout={logout}
                              currentUser={currentUser}
                              patient={patient}
                              config={patientLayout}
                              resolveUserNonRoleWidgets={
                                hasConfigServiceDashboardWidgets
                                  ? patientPalette
                                  : undefined
                              }
                              topBarHeight={height}
                              {...props}
                            />
                          )}
                        />
                      )}
                      {dashboardType === clinicalDashboard && (
                        <Route
                          path="/"
                          render={(props: any) => (
                            <DashboardComponent
                              pim={pim}
                              keycloak={keycloak}
                              logout={logout}
                              currentUser={currentUser}
                              patient={patient}
                              config={clinicalLayout}
                              resolveUserNonRoleWidgets={
                                hasConfigServiceDashboardWidgets
                                  ? clinicalPalette
                                  : undefined
                              }
                              topBarHeight={height}
                              {...props}
                            />
                          )}
                        />
                      )}
                    </Switch>
                  </AppContent>
                )}
              </HeightProvider>
              {patient && <PrintFooter patient={patient} />}
            </AppRoot>
          );
        }}
      </GlobalContext.Consumer>
    );
  }
}

export default withRouter(App);
